import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import type { LeadRecord, LeadStatus } from '@flashpoint/site-dna';
import { resolveLook, sitemapCrumbs } from '@flashpoint/site-dna';
import { onLeadSnapshot, updateLeadStatus } from '../lib/firestore';
import { logHistory } from '../lib/historyLog';
import { useAuth } from '../auth/AuthContext';
import { StatusPipelineRail } from '../components/StatusPipelineRail';
import { PreviewLink } from './lead/PreviewLink';

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [lead, setLead] = useState<LeadRecord | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    return onLeadSnapshot(id, setLead);
  }, [id]);

  if (lead === undefined) return <div className="font-mono text-sm text-nm-muted">Loading…</div>;
  if (lead === null) {
    return (
      <div className="fp-card p-10 text-center">
        <h2 className="font-display text-lg font-semibold text-nm-ink">Preview not found</h2>
        <Link to="/leads" className="mt-4 inline-block font-mono text-sm text-nm-accent hover:underline">
          Back to previews
        </Link>
      </div>
    );
  }

  const onStatusChange = async (next: LeadStatus) => {
    if (next === lead.leadStatus) return;
    const prev = lead.leadStatus;
    await updateLeadStatus(lead.id, next);
    logHistory({
      leadId: lead.id,
      user,
      kind: 'status_change',
      payload: { from: prev, to: next },
    });
  };

  const name = lead.siteDna.businessInfo.name?.trim() || '(untitled)';
  const industry = lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical || '';
  const look = resolveLook(
    industry,
    lead.siteDna.designTokens.personality ?? [],
    lead.siteDna.designTokens.lookVariant ?? 0,
    lead.siteDna.designTokens.density,
  );

  return (
    <div className="space-y-5">
      <Link to="/leads" className="fp-label inline-block hover:text-nm-accent">
        ← All previews
      </Link>

      <div className="fp-card space-y-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-nm-ink sm:text-3xl">{name}</h1>
          <p className="mt-1 text-sm text-nm-muted">
            {industry || 'No industry yet'} · {look.label}
          </p>
          <p className="mt-1 text-sm text-nm-muted">{sitemapCrumbs(lead.siteDna.layout)}</p>
        </div>
        <PreviewLink hostname={lead.provisioning.hostname} />
        <div className="flex flex-wrap gap-3 border-t border-nm-border pt-3">
          {lead.provisioning?.repo && !lead.provisioning.codeLocked && (
            <Link to={`/leads/${lead.id}/edit`} className="text-xs text-nm-tertiary hover:text-nm-accent">
              Edit files
            </Link>
          )}
          <Link to={`/leads/${lead.id}/wizard`} className="text-xs text-nm-tertiary hover:text-nm-accent">
            Old wizard
          </Link>
        </div>
      </div>

      <div className="fp-card">
        <StatusPipelineRail current={lead.leadStatus} onChange={(s) => void onStatusChange(s)} />
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-nm-border">
        {[
          { to: 'overview', label: 'Call sheet' },
          { to: 'provisioning', label: 'Preview' },
          { to: 'history', label: 'History' },
        ].map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `rounded-t-[10px] border-b-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                isActive
                  ? 'border-nm-accent bg-nm-accent-tint text-nm-accent'
                  : 'border-transparent text-nm-muted hover:text-nm-ink'
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ lead }} />
    </div>
  );
}
